import { Button, ButtonProps } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { ReactNode, useMemo } from 'react';
import { OAuthFormData } from 'v2/core/entities/integrations';
import useGetIntegrationsFormData from 'v2/core/repository/edge-server/integrations/useGetIntegrationsFormData';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';
import { CanAccess } from '../CanAccess';
import { getRedirectUriForSlackIntegration } from './utils/getRedirectUriForSlackIntegration';

interface ConnectSlackButtonProps {
  children: ReactNode;
  buttonProps?: ButtonProps;
  redirectRouteOnConnect?: string;
}

export default function ConnectSlackButton({
  children,
  buttonProps,
  redirectRouteOnConnect,
}: ConnectSlackButtonProps) {
  const user = useGetUser();

  const { data: integrationFormData, status: integrationFormStatus } =
    useGetIntegrationsFormData();

  const privateSlackForm = useMemo(() => {
    return integrationFormData?.find(
      (form) =>
        form.direction === IntegrationDirection.PrivateOutbound &&
        form.channel.type === IntegrationChannelType.Slack,
    ) as OAuthFormData | undefined;
  }, [integrationFormData]);

  if (IsLoading(integrationFormStatus)) {
    return (
      <Button {...buttonProps} disabled>
        {children}
      </Button>
    );
  }

  if (!privateSlackForm) {
    return null;
  }

  return (
    <CanAccess
      resource={PermissionResource.OutboundIntegration}
      operation={PermissionOperation.Write}
    >
      <Button
        {...buttonProps}
        href={getRedirectUriForSlackIntegration(
          privateSlackForm.oAuthURL,
          privateSlackForm.channel.type,
          privateSlackForm.direction,
          user.tenant.slug,
          redirectRouteOnConnect,
        )}
      >
        {children}
      </Button>
    </CanAccess>
  );
}
